import classNames from 'classnames';
import type { FunctionComponent, ReactNode, VoidFunctionComponent } from 'react';

import Badge from '../../../generic/Badge/Badge';
import { DetailsPanelLink } from '../../../generic/DetailsPanel';
import Radio from '../../../generic/Forms/Radio';

import { UPGRADE_BADGE } from './upgradeBadges';
import type { FossabotFixOption } from './useFossabotFixOptions';

import styles from './FossabotFixOptionCard.module.less';

type Props = {
  option: FossabotFixOption;
  /** CVE identifier, linked in the partial-fix description. */
  cve: string;
  /**
   * Unique radio value for this card. The same version can appear in more than
   * one group (e.g. FOSSABOT PR and CREATE TICKET), so the version alone is not
   * unique enough.
   */
  value: string;
  isSelected: boolean;
  onSelect: () => void;
};

type FossabotSectionLabelProps = {
  children: ReactNode;
};

/**
 * Small grey uppercase label above a group of fix option cards (CREATE TICKET,
 * FOSSABOT PR, TICKET CREATED). The copy itself is written in uppercase.
 */
export const FossabotSectionLabel: FunctionComponent<FossabotSectionLabelProps> = ({ children }) => (
  <span className={styles.sectionLabel}>{children}</span>
);

type FossabotFixBadgesProps = Pick<FossabotFixOption, 'distance' | 'isComplete'>;

/**
 * The upgrade-size + fix-type badge pair shown next to an "Upgrade to
 * {version}" heading. The upgrade-size badge is omitted when the distance
 * could not be calculated.
 */
export const FossabotFixBadges: VoidFunctionComponent<FossabotFixBadgesProps> = ({
  distance,
  isComplete,
}) => {
  const upgradeBadge = distance ? UPGRADE_BADGE[distance] : null;

  return (
    <span className={styles.badges}>
      {upgradeBadge && (
        <Badge color="white" icon={upgradeBadge.icon}>
          {upgradeBadge.label}
        </Badge>
      )}
      <Badge color={isComplete ? 'dark-green' : 'light-green'}>
        {isComplete ? 'Complete Fix' : 'Partial Fix'}
      </Badge>
    </span>
  );
};

/**
 * A radio-selectable upgrade option card in the Remediation panel: radio,
 * "Upgrade to {version}" heading, upgrade-size + fix-type badges, and a
 * one-line description. The whole card is a label, so clicking anywhere on
 * it selects the radio.
 */
export const FossabotFixOptionCard: VoidFunctionComponent<Props> = ({ option, cve, value, isSelected, onSelect }) => {
  const { version, distance, isComplete } = option;
  const radioId = `fix-option-${value}`;

  return (
    <label
      className={classNames(styles.card, { [styles.cardSelected]: isSelected })}
      htmlFor={radioId}
      data-test-id={radioId}
    >
      <Radio id={radioId} value={value} isChecked={isSelected} onChange={onSelect} isRippleDisabled />
      <span className={styles.content}>
        <span className={styles.topRow}>
          <span className={styles.heading}>Upgrade to {version}</span>
          <FossabotFixBadges distance={distance} isComplete={isComplete} />
        </span>
        <span className={styles.description}>
          {isComplete ? (
            'Fixes all known vulnerabilities on this dependency.'
          ) : (
            <>
              Fixes{' '}
              <DetailsPanelLink to={`https://nvd.nist.gov/vuln/detail/${cve}`}>{cve}</DetailsPanelLink>
              {' '}only.
            </>
          )}
        </span>
      </span>
    </label>
  );
};
